import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { reduxForm } from 'redux-form';
import uniqBy from 'lodash/uniqBy';
import reverse from 'lodash/reverse';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { FormUi, Flex, Grid, Field, Menu, Header, Button } from 'nallian-shared-ui/lib/components';
import { TextAreaFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import AddBlobLanguage from './AddBlobLanguage';

const MANAGE_BLOB_RIGHTS = ['community-blobs/create', 'community-blobs/update'];

const HiddenGridColumn = styled(Grid.Column)`
  ${props =>
    !props.active &&
    css`
      display: none !important;
    `}
`;

const BlobsForm = ({ handleSubmit, onSubmit, languages, submitting }) => {
  const { t } = useTranslation();
  const [headerLanguages, setHeaderLanguages] = useState(languages);
  const [footerLanguages, setFooterLanguages] = useState(languages);

  const handleSetLanguages = useCallback(
    (_, { name, isNew, header }) => {
      const execute = header ? setHeaderLanguages : setFooterLanguages;
      const mappedLanguages = header ? headerLanguages : footerLanguages;
      if (isNew)
        execute(
          reverse(
            uniqBy(
              reverse([
                ...mappedLanguages.map(hl => ({ ...hl, active: false })),
                { id: name, active: true },
              ]),
              'id'
            )
          )
        );
      else execute(mappedLanguages.map(hl => ({ ...hl, active: hl.id === name })));
    },
    [headerLanguages, footerLanguages, setHeaderLanguages, setFooterLanguages]
  );

  return (
    <Flex fluid>
      <FormUi fluid onSubmit={handleSubmit(onSubmit)}>
        <Header style={{ marginTop: '0' }} dividing>
          {t('emailNotificationHeader')}
        </Header>
        <Menu pointing secondary>
          {(headerLanguages || []).map(hl => (
            <Menu.Item
              header
              name={hl.id}
              key={hl.id}
              content={hl.id}
              active={hl.active}
              onClick={handleSetLanguages}
            />
          ))}
          <AddBlobLanguage header callback={handleSetLanguages} />
        </Menu>
        <Grid>
          {headerLanguages.map(hl => (
            <HiddenGridColumn active={hl.active} key={hl.id} mobile={16} tablet={16} computer={16}>
              <Field
                fluid
                disabled={submitting}
                name={`header.${hl.id}`}
                valueComponent={TextAreaFieldValueComponent}
                rows={12}
              />
            </HiddenGridColumn>
          ))}
        </Grid>
        <Header style={{ marginTop: '0' }} dividing>
          {t('emailNotificationFooter')}
        </Header>
        <Menu pointing secondary>
          {(footerLanguages || []).map(fl => (
            <Menu.Item
              footer
              name={fl.id}
              key={fl.id}
              content={fl.id}
              active={fl.active}
              onClick={handleSetLanguages}
            />
          ))}
          <AddBlobLanguage footer callback={handleSetLanguages} />
        </Menu>
        <Grid>
          {footerLanguages.map(fl => (
            <HiddenGridColumn active={fl.active} key={fl.id} mobile={16} tablet={16} computer={16}>
              <Field
                fluid
                disabled={submitting}
                name={`footer.${fl.id}`}
                valueComponent={TextAreaFieldValueComponent}
                rows={12}
              />
            </HiddenGridColumn>
          ))}
        </Grid>
        <HasSomeRights rights={MANAGE_BLOB_RIGHTS}>
          <Flex justifyContent="flex-end" margin="1rem 0 0">
            <Button
              btnprimary
              loading={submitting}
              disabled={submitting}
              onClick={handleSubmit(onSubmit)}
              icon="save"
              content={t('save')}
            />
          </Flex>
        </HasSomeRights>
      </FormUi>
    </Flex>
  );
};

BlobsForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  languages: PropTypes.array,
};

BlobsForm.defaultProps = { languages: [] };

export default compose(
  reduxForm({
    validate: () => {},
    form: 'blobs',
    enableReinitialize: true,
  })
)(BlobsForm);
